import React, { useCallback } from 'react';
import type { PageDetailOut } from '../../types/catalog';
import type { ContentWithSections, SectionHeading, SectionParagraph, SectionExamples, ExampleItem } from '../../types/pageTypes';
import { Volume2 } from 'lucide-react';
import { speakTamil } from '../../utils/tts';

interface PhonicsConceptV2Props {
  page: PageDetailOut;
  onComplete?: () => void;
}

function getSections(page: PageDetailOut) {
  const content = page.page_json_v1?.content as ContentWithSections | undefined;
  return content?.sections ?? [];
}

export const PhonicsConceptV2: React.FC<PhonicsConceptV2Props> = ({ page }) => {
  const sections = getSections(page);
  const heading = sections.find((s) => s.type === 'heading') as SectionHeading | undefined;
  const paragraph = sections.find((s) => s.type === 'paragraph') as SectionParagraph | undefined;
  const examples = sections.find((s) => s.type === 'examples') as SectionExamples | undefined;
  const items = examples?.items ?? examples?.rows ?? [];

  const playResult = useCallback((row: ExampleItem) => {
    const t = row.result ?? [row.lhs, row.rhs].filter(Boolean).join(' ');
    if (t) speakTamil(t);
  }, []);

  return (
    <div className="max-w-4xl mx-auto p-6 space-y-6">
      <h1 className="text-2xl font-semibold text-gray-900 dark:text-white">
        {page.page_title || heading?.text || 'Concept'}
      </h1>

      {heading?.text && (
        <h2 className="text-xl font-medium text-gray-800 dark:text-gray-200">
          {heading.text}
        </h2>
      )}

      {paragraph?.text && (
        <p className="text-lg text-gray-700 dark:text-gray-300 leading-relaxed max-w-2xl whitespace-pre-line">
          {paragraph.text}
        </p>
      )}

      {items.length > 0 && (
        <div className="space-y-3">
          {examples?.title && (
            <h3 className="text-lg font-medium text-gray-900 dark:text-white">{examples.title}</h3>
          )}
          <div className="grid gap-3">
            {items.map((row, i) => (
              <button
                key={i}
                type="button"
                onClick={() => playResult(row)}
                className="flex flex-wrap items-center justify-between gap-2 p-4 rounded-xl bg-white dark:bg-gray-800 border-2 border-gray-200 dark:border-gray-700 hover:border-primary-400 dark:hover:border-primary-500 text-left transition-colors"
              >
                <span className="text-lg text-gray-600 dark:text-gray-400">
                  {[row.lhs, row.op, row.rhs].filter(Boolean).join(' ')}
                </span>
                <span className="text-2xl font-semibold text-gray-900 dark:text-white">
                  {row.result ?? '—'}
                </span>
                <Volume2 className="w-5 h-5 text-primary-500 shrink-0" />
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};
